import type { ThunkAction, UnknownAction } from '@reduxjs/toolkit';
import { baseProfileApi } from '@src/core/features';
import type { AssignableDrone, AssignableUser, HubEntity, ICreateHubRequest, IUpdateHubRequest } from '../entities';
import { invalidateHubScopedCaches } from './invalidate-hub-scoped-caches';

const updateHubsCache = baseProfileApi.util.updateQueryData as unknown as (
  endpointName: 'getHubs',
  arg: undefined,
  updateRecipe: (draft: HubEntity[]) => void,
) => ThunkAction<{ undo: () => void }, unknown, unknown, UnknownAction>;

export const hubsApi = baseProfileApi.injectEndpoints({
  endpoints: (builder) => ({
    getHubs: builder.query<HubEntity[], void>({
      query: () => ({
        url: '/api/v1/hubs',
        method: 'GET',
      }),
      providesTags: ['Hubs'],
    }),

    getActiveHub: builder.query<HubEntity, void>({
      query: () => ({
        url: '/api/v1/hubs/active',
        method: 'GET',
      }),
      providesTags: ['Hubs', 'Profile'],
    }),

    getHubById: builder.query<HubEntity, number>({
      query: (hubId) => ({
        url: `/api/v1/hubs/${hubId}`,
        method: 'GET',
      }),
      providesTags: ['Hubs'],
    }),

    selectHub: builder.mutation<HubEntity, number>({
      query: (hubId) => ({
        url: `/api/v1/hubs/${hubId}/select`,
        method: 'POST',
      }),
      invalidatesTags: ['Hubs', 'Profile'],
      async onQueryStarted(hubId, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          updateHubsCache('getHubs', undefined, (draft) => {
            const selectedIndex = draft.findIndex((hub) => hub.id === hubId);
            if (selectedIndex === -1) return;

            draft.forEach((hub) => {
              hub.selected = hub.id === hubId;
            });

            const [selectedHub] = draft.splice(selectedIndex, 1);
            draft.unshift(selectedHub);
          }),
        );

        try {
          await queryFulfilled;
          invalidateHubScopedCaches(dispatch);
        } catch {
          patchResult.undo();
        }
      },
    }),

    createHub: builder.mutation<HubEntity, ICreateHubRequest>({
      query: (body) => ({
        url: '/api/v1/hubs',
        method: 'POST',
        data: body,
      }),
      invalidatesTags: ['Hubs', 'Profile'],
    }),

    updateHub: builder.mutation<HubEntity, { hubId: number; body: IUpdateHubRequest }>({
      query: ({ hubId, body }) => ({
        url: `/api/v1/hubs/${hubId}`,
        method: 'PUT',
        data: body,
      }),
      invalidatesTags: ['Hubs', 'Profile'],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          invalidateHubScopedCaches(dispatch);
        } catch {
          /* errors handled at call site */
        }
      },
    }),

    deleteHub: builder.mutation<void, number>({
      query: (hubId) => ({
        url: `/api/v1/hubs/${hubId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Hubs', 'Profile'],
    }),

    getAssignableUsers: builder.query<AssignableUser[], number | undefined>({
      query: (hubId) => ({
        url: '/api/v1/hubs/assignable-users',
        method: 'GET',
        params: hubId != null ? { hubId } : undefined,
      }),
    }),

    getAssignableDrones: builder.query<AssignableDrone[], number | undefined>({
      query: (hubId) => ({
        url: '/api/v1/hubs/assignable-drones',
        method: 'GET',
        params: hubId != null ? { hubId } : undefined,
      }),
    }),
  }),
});

export const {
  useGetHubsQuery,
  useGetActiveHubQuery,
  useGetHubByIdQuery,
  useSelectHubMutation,
  useCreateHubMutation,
  useUpdateHubMutation,
  useDeleteHubMutation,
  useGetAssignableUsersQuery,
  useGetAssignableDronesQuery,
} = hubsApi;
